import React, { useState, useRef, useEffect } from 'react';
import { FaFileAlt, FaImage, FaInfoCircle } from 'react-icons/fa';

const TextAndLogo = ({ mergedVideoPath }) => {
  const [text, setText] = useState('Fantastic Experience!');
  const [fontSize, setFontSize] = useState(32);
  const [fontColor, setFontColor] = useState('#FFFFFF');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [logo, setLogo] = useState('');
  const [logoSize, setLogoSize] = useState(100);
  const [logoPosition, setLogoPosition] = useState({ x: 10, y: 10 });
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const logoRef = useRef(null);
  const [logoHasShadow, setLogoHasShadow] = useState(false);
  const [logoShadowColor, setLogoShadowColor] = useState('rgba(0,0,0,0.5)');
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const textRef = useRef(null);
  const containerRef = useRef(null);

  const handleMouseDown = (e) => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (isDragging && textRef.current && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const textRect = textRef.current.getBoundingClientRect();
      let newX = e.clientX - containerRect.left - textRect.width / 2;
      let newY = e.clientY - containerRect.top - textRect.height / 2;

      // Constrain within the container
      newX = Math.max(0, Math.min(newX, containerRect.width - textRect.width));
      newY = Math.max(0, Math.min(newY, containerRect.height - textRect.height));

      setPosition({ x: (newX / containerRect.width) * 100, y: (newY / containerRect.height) * 100 });
    }

    if (isDraggingLogo && logoRef.current && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const logoRect = logoRef.current.getBoundingClientRect();
      let newX = e.clientX - containerRect.left - logoRect.width / 2;
      let newY = e.clientY - containerRect.top - logoRect.height / 2;

      // Constrain within the container
      newX = Math.max(0, Math.min(newX, containerRect.width - logoRect.width));
      newY = Math.max(0, Math.min(newY, containerRect.height - logoRect.height));

      setLogoPosition({ x: (newX / containerRect.width) * 100, y: (newY / containerRect.height) * 100 });
    }
  };

  useEffect(() => {
    const handleMouseUpGlobal = () => {
      setIsDragging(false);
      setIsDraggingLogo(false);
    };

    if (isDragging || isDraggingLogo) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUpGlobal);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUpGlobal);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  }, [isDragging, isDraggingLogo]);

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4 flex" role="alert">
        <FaInfoCircle className="mr-2 mt-1" />
        <div>
          <p className="font-bold">Under Development</p>
          <p>This feature is currently under development and will be available soon.</p>
        </div>
      </div>
      <div className="w-full">
        <div className="max-w-3xl mx-auto relative" ref={containerRef}>
          <div className="aspect-w-16 aspect-h-9 rounded-2xl overflow-hidden">
            <video autoPlay loop muted src={mergedVideoPath} className="w-full h-full object-cover">
            </video>
          </div>
          <div
              ref={textRef}
              className={`absolute cursor-move ${isDragging ? 'border-2 border-dashed border-white' : ''}`}
              style={{
                top: `${position.y}%`,
                left: `${position.x}%`,
                fontSize: `${fontSize}px`,
                color: fontColor,
                fontFamily: fontFamily,
                fontWeight: isBold ? 'bold' : 'normal',
                fontStyle: isItalic ? 'italic' : 'normal',
                textDecoration: isUnderline ? 'underline' : 'none',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                opacity: opacity,
              }}
              onMouseDown={handleMouseDown}
            >
              {text}
            </div>
            {logo && (
              <img
                ref={logoRef}
                src={logo}
                alt="logo"
                className={`absolute cursor-move ${isDraggingLogo ? 'border-2 border-dashed border-white' : ''}`}
                style={{
                  top: `${logoPosition.y}%`,
                  left: `${logoPosition.x}%`,
                  width: `${logoSize}px`,
                  filter: logoHasShadow ? `drop-shadow(2px 2px 4px ${logoShadowColor})` : 'none',
                }}
                onMouseDown={() => setIsDraggingLogo(true)}
              />
            )}
        </div>
      </div>
      <div className="flex flex-row gap-4">
        <div className="w-1/2 p-4 rounded-2xl bg-gray-100">
          <h2 className="text-xl font-bold mb-2 text-gray-800 flex items-center gap-2"><FaFileAlt /> Text</h2>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full p-2 rounded bg-white text-gray-800 mb-2"
          />
          <div className="flex items-center flex-wrap gap-2 p-2 rounded-md bg-gray-200">
            <select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              className="p-1 rounded bg-white text-gray-800 border border-gray-300"
            >
              <option value="Arial">Arial</option>
              <option value="Courier New">Courier New</option>
              <option value="Georgia">Georgia</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Verdana">Verdana</option>
            </select>
            <div className="h-6 border-l border-gray-300 mx-2"></div>
            <button onClick={() => setFontSize(s => Math.max(1, s - 1))} className="px-2 text-lg">-</button>
            <input
              type="number"
              value={fontSize}
              onChange={(e) => setFontSize(parseInt(e.target.value))}
              className="w-12 text-center p-1 rounded border border-gray-300"
            />
            <button onClick={() => setFontSize(s => s + 1)} className="px-2 text-lg">+</button>
            <div className="h-6 border-l border-gray-300 mx-2"></div>
            <button onClick={() => setIsBold(!isBold)} className={`w-8 h-8 flex items-center justify-center rounded ${isBold ? 'bg-gray-400' : 'bg-white'}`}>B</button>
            <button onClick={() => setIsItalic(!isItalic)} className={`w-8 h-8 flex items-center justify-center rounded ${isItalic ? 'bg-gray-400' : 'bg-white'}`}>I</button>
            <button onClick={() => setIsUnderline(!isUnderline)} className={`w-8 h-8 flex items-center justify-center rounded ${isUnderline ? 'bg-gray-400' : 'bg-white'}`}>U</button>
            <div className="h-6 border-l border-gray-300 mx-2"></div>
            <input
              type="color"
              value={fontColor}
              onChange={(e) => setFontColor(e.target.value)}
              className="w-8 h-8"
            />
          </div>
          <div className="flex items-center gap-2 mt-2 text-gray-800">
            <label>Opacity:</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
        <div className="w-1/2 p-4 rounded-2xl bg-gray-100">
          <h2 className="text-xl font-bold mb-2 text-gray-800 flex items-center gap-2"><FaImage /> Logo</h2>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setLogo(URL.createObjectURL(e.target.files[0]));
              }
            }}
            className="w-full p-2 rounded bg-white text-gray-800 mb-2"
          />
          <input
            type="text"
            placeholder="Or paste image URL"
            onPaste={(e) => {
              const pastedText = e.clipboardData.getData('text');
              setLogo(pastedText);
            }}
            className="w-full p-2 rounded bg-white text-gray-800"
          />
          <div className="flex items-center gap-2 mt-2">
            <input
              type="checkbox"
              checked={logoHasShadow}
              onChange={(e) => setLogoHasShadow(e.target.checked)}
              id="logo-shadow"
            />
            <label htmlFor="logo-shadow" className="text-gray-800">Add shadow</label>
          </div>
          {logoHasShadow && (
            <div className="flex items-center gap-2 mt-2">
              <label htmlFor="logo-shadow-color" className="text-gray-800">Shadow Color:</label>
              <input
                type="color"
                id="logo-shadow-color"
                value={logoShadowColor}
                onChange={(e) => setLogoShadowColor(e.target.value)}
                className="w-8 h-8"
              />
            </div>
          )}
          <div className="flex items-center gap-2 mt-2 text-gray-800">
            <label>Size:</label>
            <input
              type="range"
              min="20"
              max="300"
              value={logoSize}
              onChange={(e) => setLogoSize(parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextAndLogo;
